import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, of, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { CmsService } from '@spartacus/core';
import { IFilter, IOrder, SdFiltersComponent } from '@seb/web-design-system/filters';
import { IRecipeCard, SdRecipeCardComponent } from '@seb/web-design-system/recipe-card';
import { SdPaginationComponent } from '@seb/web-design-system/pagination';
import {
  DeviceService,
  ISdFormComponentItem,
  ISliderInfo,
  SdFormInputComponent,
  SizesEnum
} from '@seb/web-design-system/core';
import { SdShowMoreComponent } from '@seb/web-design-system/show-more';
import {
  DcpListResponse,
  DcpRecipeSearchRequestBodyFacetFilter,
  DcpRecipeSearchRequestBodyFieldFilter, DcpResponseFacet, DcpResponseFacetEntry, DcpResponsePagination,
  recipeCardSkeleton, RecipeSearchItem,
  RecipeSearchParamsUrl
} from '../../../models';
import {
  BaseStoreService,
  LazyLoadingService,
  PaginationComponentService,
  RecipeService,
  TrackingService
} from '../../../services';

interface IFilterWithKeyType extends IFilter {
  key: string;
  type: string;
}

interface SortItem {
  code: string;
  name: string;
  selected: boolean;
  direction: 'ASC' | 'DESC';
}

export interface SearchRecipes {
  recipes: DcpListResponse<RecipeSearchItem>;
  searchParams: RecipeSearchParamsUrl;
  query: string;
}

@Component({
  selector: 'wc-recipe-search',
  templateUrl: './recipe-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdFiltersComponent,
    SdFormInputComponent,
    FormsModule,
    ReactiveFormsModule,
    SdRecipeCardComponent,
    SdPaginationComponent,
    TranslatePipe
  ]
})
export class RecipeSearchComponent implements OnInit, OnDestroy {
  sizesEnum = SizesEnum;

  isLoading = true;
  useLazyLoad = false;
  isInit = true;
  linkUrl = '';
  titleTag = '';
  isRecipeCategoryPage = false;

  currentPage = 0;
  public isMobile$: boolean;
  public pagination: DcpResponsePagination = {
    size: 12,
    totalElements: 0,
    totalPages: 0,
    number: -1
  };
  public recipes: IRecipeCard[];
  public filters: IFilterWithKeyType[] = [];
  public orderSelectConfig: IOrder;

  @Input() hasControl = false;
  formControl = new FormControl('');

  @Input()
  alreadySearchedRecipe?: SearchRecipes;

  @Output()
  alreadySearchedRecipeChange = new EventEmitter<SearchRecipes>();

  @ViewChild(SdShowMoreComponent)
  showMoreComponent: SdShowMoreComponent;

  private readonly destroy$: Subject<void> = new Subject();
  private readonly searchSubject$: Subject<number> = new BehaviorSubject(0);
  private mobileSubscription: Subscription;
  private recipeSearchParams: RecipeSearchParamsUrl = {};
  private recipeSearchQuery: string;
  constructor(
    private recipeService: RecipeService,
    private deviceService: DeviceService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private viewportScroller: ViewportScroller,
    private paginationComponentService: PaginationComponentService,
    private lazyLoadingService: LazyLoadingService,
    private cmsService: CmsService,
    private baseStoreService: BaseStoreService,
    @Inject(PLATFORM_ID) protected platformId: any
  ) {
    this.useLazyLoad = this.lazyLoadingService.isEnabled;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.viewportScroller.setOffset([0, 200]);
    }
    this.mobileSubscription = this.deviceService.isMobile.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile$ = isMobile;
      this.cdRef.markForCheck();
    });
    this.paginationComponentService.getUrlWithParams().subscribe((result) => {
      this.linkUrl = result;
    });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetSearch();
    });

    this.isInit = true;
    const pagetitleTage$ = this.cmsService.getCurrentPage().pipe(
      filter((p) => !!p),
      map((p) => (p['titleTag'] ? p['titleTag'] : p?.title))
    );
    const page$ = this.cmsService.getCurrentPage();
    if (!this.lazyLoadingService.isForced) {
      combineLatest([
        this.route.queryParamMap,
        this.searchSubject$,
        pagetitleTage$,
        page$
      ])
        .pipe(
          switchMap(([params, searchPage, currentPage, page]) => {
            const baseStore = this.baseStoreService.baseStore;
            this.isLoading = true;
            const pageNumber = params.has('page') ? parseInt(params.get('page')) - 1 : searchPage;
            this.titleTag = currentPage.replace(/<siteBrand>/gi, (site) =>
              /[a-z]/.test(site)
                ? `${baseStore['brandSite']?.charAt(0).toUpperCase()}${baseStore['brandSite']?.toLowerCase().slice(1)}`
                : baseStore['brandSite']?.toUpperCase()
            );
            this.recipeSearchParams = params.has('sort')
              ? this.recipeService.queryParamsToRecipeSearchParams(params)
              : {
                sort: {
                  name: 'score',
                  direction: 'DESC'
                }
              };
            if (page['externalRecipeCategoryFilter']) {
              this.isRecipeCategoryPage = true;
              this.recipeSearchParams = {
                ...this.recipeSearchParams,
                'filters': [
                  {
                    'field': 'domain.key',
                    'values': [
                      page['externalRecipeCategoryFilter']
                    ]
                  }
                ]
              };
            } else {
              this.isRecipeCategoryPage = false;
            }
            this.recipeSearchQuery = params.get('query');
            this.formControl.setValue(this.recipeSearchQuery);
            if (
              this.alreadySearchedRecipe === undefined ||
              this.alreadySearchedRecipe?.searchParams?.facets?.toString() !==
              this.recipeSearchParams?.facets?.toString() ||
              this.alreadySearchedRecipe?.searchParams?.filters?.toString() !==
              this.recipeSearchParams?.filters?.toString() ||
              this.alreadySearchedRecipe?.searchParams?.sort?.name !== this.recipeSearchParams?.sort?.name ||
              this.recipeSearchQuery !== this.alreadySearchedRecipe.query ||
              pageNumber !== this.pagination.number
            ) {
              return this.recipeService.search(
                params.get('query')?.split(':')[0]?.trim() || '',
                pageNumber,
                this.pagination.size,
                this.recipeSearchParams
              );
            } else {
              return of(this.alreadySearchedRecipe.recipes);
            }
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((searchResult: DcpListResponse<RecipeSearchItem>) => {
          this.alreadySearchedRecipeChange.emit({
            recipes: searchResult,
            searchParams: this.recipeSearchParams,
            query: this.recipeSearchQuery
          });
          const facets = this.isRecipeCategoryPage
            ? searchResult.facets.filter((facet) => facet.facet !== 'domain')
            : searchResult.facets;
          this.mapFilters(facets);
          this.mapOrderBy();
          this.pagination = searchResult.page;
          if (searchResult.content?.length > 0) {
            combineLatest(
              searchResult.content.map((item: RecipeSearchItem) => this.recipeService.extractRecipeSearchItem(item))
            )
              .pipe(take(1))
              .subscribe((results: IRecipeCard[]) => {
                this.recipes = results ?? [];
                this.isLoading = false;
                this.cdRef.markForCheck();
              });
          } else {
            this.recipes = [];
            this.isLoading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  search(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.formControl.value,
        sort: 'score:DESC'
      },
      queryParamsHandling: 'merge'
    });

    this.trackingService.sendEvent('search', {
      request: this.formControl.value,
      type: 'not set'
    });
  }

  resetSearch(): void {
    this.isLoading = true;
    this.pagination = {
      size: 12,
      totalElements: 0,
      totalPages: 0,
      number: -1
    };
    if (this.useLazyLoad) {
      this.recipes = new Array(this.isMobile$ ? 4 : 12).fill(undefined).map(
        (_, i) => ({ ...recipeCardSkeleton, id: `${i}` })
      );
    }
  }

  goToPage(event: ISliderInfo): void {
    const page = event?.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: page ? page + 1 : null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectedFilters(value: string | null | undefined): void {
    // value is null for clear all filters, undefined for all durations selection (placeholder selection)
    const newRecipeSearchFacets: DcpRecipeSearchRequestBodyFacetFilter[] =
      value !== null
        ? this.filters
          .filter((filter: IFilterWithKeyType) => filter?.type === 'facet')
          .reduce((searchFacets: DcpRecipeSearchRequestBodyFacetFilter[], filter: IFilterWithKeyType) => {
            // Necessary (ugly) check due to spec request: Appliance filter only appears if domain checked
            if (filter.key !== 'applianceGroups' || this.isDomainFilterChecked()) {
              filter.values.forEach((filterValueObj: ISdFormComponentItem) => {
                if (filterValueObj.checked) {
                  searchFacets.push({
                    facet: filter.key,
                    key: filterValueObj.value as string,
                    type: 'inclusion'
                  });
                }
              });
            }
            return searchFacets;
          }, [])
        : [];

    const newRecipeSearchFilters: DcpRecipeSearchRequestBodyFieldFilter[] =
      value != null
        ? this.filters
          .filter((filter: IFilterWithKeyType) => filter?.type === 'filter')
          .reduce((searchFilters: DcpRecipeSearchRequestBodyFieldFilter[], filter: IFilterWithKeyType) => {
            if (filter.key === 'durations.totalTime') {
              const selectedDurationFilterValue: ISdFormComponentItem | undefined = filter.values.find(
                (filterValueObj: ISdFormComponentItem) => filterValueObj.checked
              );
              if (
                selectedDurationFilterValue &&
                (selectedDurationFilterValue.value as string).split(':').length > 1
              ) {
                // extract duration and match
                const selectedDurationValue: string = (selectedDurationFilterValue.value as string).split(':')[0];
                const selectedDurationMatch: string = (selectedDurationFilterValue.value as string).split(':')[1];
                searchFilters.push({
                  field: filter.key,
                  values: [selectedDurationValue],
                  match: selectedDurationMatch
                });
              }
            }
            return searchFilters;
          }, [])
        : [];

    const queryParams = this.recipeService.recipeSearchParamsToQueryParams({
      filters: newRecipeSearchFilters,
      facets: newRecipeSearchFacets,
      sort: this.recipeSearchParams.sort
    });
    const currentPage = this.isInit ? this.route.snapshot?.queryParamMap?.get('page') : 0;
    this.isInit = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...queryParams,
        query: this.recipeSearchQuery,
        target: this.route.snapshot?.queryParamMap?.get('target'),
        page: currentPage ? currentPage : null
      }
    });
  }

  onSortSelected(value: string | undefined): void {
    if (!!value && value !== this.recipeSearchParams.sort.name) {
      const newSort = {
        name: value,
        direction: this.orderSelectConfig.items.find((item: SortItem) => item.code === value)?.direction || 'DESC'
      };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          sort: `${newSort.name}:${newSort.direction}`,
          currentPage:
            !this.route.snapshot?.queryParams['sort'] && this.route.snapshot?.queryParams['page']
              ? this.route.snapshot?.queryParams['page']
              : null,
          page:
            !this.route.snapshot?.queryParams['sort'] && this.route.snapshot?.queryParams['page']
              ? this.route.snapshot?.queryParams['page']
              : null
        },
        queryParamsHandling: 'merge'
      });
      this.trackingService.sendEvent('clickSort', {
        sortName: value.charAt(0).toUpperCase() + value.slice(1)
      });
    }
  }

  onRecipeClick(recipe: IRecipeCard): void {
    this.trackingService.sendEvent('clickseeRecipe', {
      recipeName: recipe.title,
      author: TrackingService.getRecipeAuthor(recipe)
    });
  }

  clearQuery(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { query: undefined },
      queryParamsHandling: 'merge'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mobileSubscription?.unsubscribe();
  }

  private mapFilters(facets: DcpResponseFacet[]): void {
    const newFilters = facets?.length
      ? facets.reduce((filters: IFilterWithKeyType[], dcpFacet: DcpResponseFacet) => {
        // Necessary (ugly) check due to spec request: Appliance filter only appears if domain checked
        if (
          dcpFacet.facet !== 'applianceGroups' ||
          this.recipeSearchParams.filters?.some(
            (f: DcpRecipeSearchRequestBodyFieldFilter) => f.field === 'domain.key'
          )
        ) {
          filters.push({
            key: dcpFacet.facet,
            type: 'facet',
            name: this.translateService.instant(`recipes.search.filters.${dcpFacet.facet}`),
            category: false,
            multiSelect: true,
            priority: 29999,
            visible: true,
            values: dcpFacet.entries.map((dcpFacetEntry: DcpResponseFacetEntry) => ({
              value: dcpFacetEntry.key,
              libelle: dcpFacetEntry.value,
              checked:
                this.recipeSearchParams.facets?.some(
                  (facetParam) => facetParam.facet === dcpFacet.facet && facetParam.key === dcpFacetEntry.key
                ) ||
                this.recipeSearchParams.filters?.some(
                  (filterParam) =>
                    filterParam.field === `${dcpFacet.facet}.key` &&
                    filterParam.values.includes(dcpFacetEntry.externalKey)
                ),
              disabled: false,
              count: dcpFacetEntry.occurrences
            }))
          });
        }
        return filters;
      }, [])
      : [];

    // Durations filter
    let durationFilterValue: string | null = null;
    const paramFilterDuration: DcpRecipeSearchRequestBodyFieldFilter | undefined =
      this.recipeSearchParams.filters?.find(
        (paramFilter: DcpRecipeSearchRequestBodyFieldFilter) => paramFilter.field === 'durations.totalTime'
      );
    if (paramFilterDuration?.values.length && paramFilterDuration.match) {
      // Assuming this filter has only one value, get the first
      durationFilterValue = `${paramFilterDuration.values[0]}:${paramFilterDuration.match}`;
    }

    newFilters.push({
      category: false,
      multiSelect: false,
      name: this.translateService.instant('recipes.search.filters.durationsTotalTime'),
      key: 'durations.totalTime',
      type: 'filter',
      priority: 29999,
      placeholder: this.translateService.instant(
        `recipes.search.filters.durations.${durationFilterValue ? 'all' : 'placeholder'}`
      ),
      values: [
        {
          value: '15:lessThan',
          libelle: 'less-than-15',
          checked: false,
          disabled: false
        },
        {
          value: '30:lessThan',
          libelle: 'less-than-30',
          checked: false,
          disabled: false
        },
        {
          value: '60:lessThan',
          libelle: 'less-than-60',
          checked: false,
          disabled: false
        },
        {
          value: '60:moreThan',
          libelle: 'more-than-60',
          checked: false,
          disabled: false
        }
      ].map((filterValue: ISdFormComponentItem) => ({
        ...filterValue,
        libelle: this.translateService.instant(`recipes.search.filters.durations.${filterValue.libelle}`),
        checked: filterValue.value === durationFilterValue
      })),
      visible: true
    });

    this.filters = newFilters;
  }

  private mapOrderBy(): void {
    const freshItems: SortItem[] = [
      {
        code: 'score',
        name: this.translateService.instant('recipes.search.sort.score'),
        selected: false,
        direction: 'DESC'
      },
      {
        code: 'popularity',
        name: this.translateService.instant('recipes.search.sort.popularity'),
        selected: false,
        direction: 'DESC'
      },
      {
        code: 'rating',
        name: this.translateService.instant('recipes.search.sort.rating'),
        selected: false,
        direction: 'DESC'
      },
      {
        code: 'modificationDate',
        name: this.translateService.instant('recipes.search.sort.modificationDate'),
        selected: false,
        direction: 'DESC'
      },
      {
        code: 'title',
        name: this.translateService.instant('recipes.search.sort.title'),
        selected: false,
        direction: 'ASC'
      }
    ];

    const selectedSortItem: SortItem | undefined = {
      ...freshItems.find((item: SortItem) => item.code === this.recipeSearchParams.sort?.name),
      selected: true
    };
    this.orderSelectConfig = {
      items: freshItems.map((item: SortItem) => ({
        ...item,
        selected: this.recipeSearchParams.sort?.name === item.code
      })),
      valueKey: 'code',
      libelleKey: 'name',
      ...(selectedSortItem && {
        orderBy: selectedSortItem
      })
    };
  }

  private isDomainFilterChecked(): boolean {
    return this.filters.some(
      (filter: IFilterWithKeyType) =>
        filter.key === 'domain' && filter.values.some((filterValueObj: ISdFormComponentItem) => filterValueObj.checked)
    );
  }

  onClickedPage(index: number) {
    if (isPlatformBrowser(this.platformId)) {
      this.viewportScroller.scrollToAnchor('recipe-card-grid-filters');
    }
    this.trackingService.sendEvent('clickPagination', { paginationLevel: index });
    this.goToPage({ index: index - 1, itemsPerView: this.pagination.size });
  }
}
